import React, { useRef, useMemo, useState, useEffect } from 'react';
import { useDeepCompareEffect } from 'ahooks';
import { Bubble } from 'tea-component';

import './index.scss';

export enum AppMagicGenreTagType {
  // 一级品类
  SuperGenre = 'super_genre',
  // 二级品类
  MajorGenre = 'major_category',
  // 三级品类
  MidGenre = 'mid_category',
  // 四级品类
  SubGenre = 'sub_category',
}

interface IAppMagicGenreTagListProps {
  /** 标签列表 */
  tags: string[];
  /** 标签展示文案格式化，点击回调仍传原始标签 */
  tagFormatter?: (tag: string) => string;
  /** 点击标签回调 */
  onTagClick?: (tag: string) => void;
}

const AppMagicGenreTagList: React.FC<IAppMagicGenreTagListProps> = (props) => {
  const { tags, tagFormatter, onTagClick } = props;

  const tagItemClassName = !!onTagClick
    ? 'dbi_appmagic_genre_tag_item dbi_appmagic_genre_tag_item--clickable'
    : 'dbi_appmagic_genre_tag_item';

  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleTags, setVisibleTags] = useState<string[]>(tags);
  const [overflowCount, setOverflowCount] = useState(0);

  // +n Bubble 提示中的 Tags
  const restTags = useMemo<string[]>(() => {
    if (!Array.isArray(tags) || tags.length === 0) {
      return [];
    }

    return tags.slice(-overflowCount);
  }, [overflowCount, tags]);

  // 初始所有 Tag 渲染后计算 +n
  useDeepCompareEffect(() => {
    const calculateOverflow = () => {
      const container = containerRef.current;
      if (!container) return;

      const children = Array.from(container.children);
      const maxHeight = container.offsetHeight;

      let currentHeight = 0;
      let visibleCount = 0;

      // 计算显示的标签，包含 +n 的占位
      for (const child of children) {
        const childRect = child.getBoundingClientRect();
        currentHeight = childRect.bottom - container.getBoundingClientRect().top;

        // 如果当前标签已经超出最大高度, 终止循环
        if (currentHeight > maxHeight) {
          break;
        }

        visibleCount += 1;
      }

      const totalTags = tags.length;
      const tagsToShow = visibleCount > totalTags ? totalTags : visibleCount;
      const remainingCount = totalTags - tagsToShow;

      const newOverflowCount = remainingCount > 0 ? remainingCount : 0;
      setOverflowCount(newOverflowCount);
      setVisibleTags(tags.slice(0, tagsToShow));
    };

    if (!Array.isArray(tags) || tags.length === 0) {
      setOverflowCount(0);
      setVisibleTags([]);
      return;
    }

    calculateOverflow();
  }, [tags]);

  // +n 渲染后，避免 +n 元素溢出，监听处理
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new MutationObserver(() => {
      const children = Array.from(container.children);

      let currentHeight = 0;
      const maxHeight = container.offsetHeight;

      const lastChild = children[children.length - 1];
      const childRect = lastChild?.getBoundingClientRect();

      currentHeight = childRect?.bottom - container.getBoundingClientRect().top;

      if (currentHeight > maxHeight) {
        setVisibleTags((prevTags) => {
          const currTags = prevTags.slice();
          currTags.pop();

          return currTags;
        });
        setOverflowCount((x) => Number(x) + 1);
      }
    });

    // 开始观察 container 子节点变化
    observer.observe(container, {
      childList: true, // 监听子节点变化
    });

    return () => observer.disconnect(); // 清理
  }, []);

  return (
    <div className="dbi_appmagic_genre_tag_list_container" ref={containerRef}>
      {visibleTags?.map((tag, index) => (
        <div
          key={index}
          className={tagItemClassName}
          onClick={(e) => {
            e.stopPropagation();
            onTagClick?.(tag);
          }}
        >
          {tagFormatter?.(tag) || tag}
        </div>
      ))}
      {Boolean(overflowCount) && (
        <Bubble
          content={
            <div className="dbi_appmagic_genre_tag_list_container_more">
              {restTags.map((tag, index) => (
                <div
                  key={index}
                  className={tagItemClassName}
                  onClick={(e) => {
                    e.stopPropagation();
                    onTagClick?.(tag);
                  }}
                >
                  {tagFormatter?.(tag) || tag}
                </div>
              ))}
            </div>
          }
        >
          <div className="dbi_appmagic_genre_tag_item">+{overflowCount}</div>
        </Bubble>
      )}
    </div>
  );
};

export default AppMagicGenreTagList;
